import React, { useEffect, useRef, useState } from 'react';

type Posture = 'advisory' | 'gated' | 'autonomous';

const OPTIONS: { key: Posture; color: string; blurb: string }[] = [
  { key: 'advisory', color: 'text-gray-400', blurb: 'Governor only recommends; nothing auto-changes.' },
  { key: 'gated', color: 'text-amber-400', blurb: 'Governor proposes; you approve inline.' },
  { key: 'autonomous', color: 'text-emerald-400', blurb: 'Governor self-executes throttles, no approval.' },
];

export const PosturePicker: React.FC<{
  current: string;
  darkMode: boolean;
  pending: boolean;
  error: string | null;
  onSelect: (p: Posture) => void;
  onClose: () => void;
}> = ({ current, darkMode, pending, error, onSelect, onClose }) => {
  const [confirm, setConfirm] = useState<Posture | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDoc);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDoc); };
  }, [onClose]);

  const pick = (p: Posture) => {
    if (pending) return;
    if (p === current) { onClose(); return; }
    if (p === 'autonomous') { setConfirm(p); return; }
    onSelect(p);
  };

  const surface = darkMode
    ? 'bg-gray-900 border-gray-700 text-gray-200'
    : 'bg-white border-gray-300 text-gray-700';

  return (
    <div ref={ref} data-testid="posture-picker"
      className={`absolute right-0 top-full mt-1 z-50 w-[230px] max-w-[230px] rounded-md border shadow-lg p-1 text-[11px] normal-case tracking-normal ${surface}`}>
      {confirm === 'autonomous' ? (
        <div data-testid="posture-confirm-autonomous" className="p-2 space-y-2">
          <p className="leading-snug">Governor will <b>self-execute</b> throttles without approval — confirm?</p>
          <div className="flex gap-2 justify-end">
            <button type="button" data-testid="posture-confirm-no" disabled={pending} onClick={() => setConfirm(null)}
              className={`px-2 py-0.5 rounded disabled:opacity-50 ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}>Cancel</button>
            <button type="button" data-testid="posture-confirm-yes" disabled={pending}
              onClick={() => { if (pending) return; onSelect('autonomous'); }}
              className="px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50">Confirm</button>
          </div>
        </div>
      ) : (
        <ul role="menu">
          {OPTIONS.map(o => (
            <li key={o.key}>
              <button type="button" role="menuitemradio" aria-checked={o.key === current}
                data-testid={`posture-opt-${o.key}`} disabled={pending} onClick={() => pick(o.key)}
                className={`w-full text-left px-2 py-1 rounded flex flex-col disabled:opacity-50 ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}>
                <span className={`uppercase tracking-wide ${o.color}`}>⚖ {o.key}{o.key === current ? ' ·' : ''}</span>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{o.blurb}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {pending && <div data-testid="posture-pending" className="px-2 py-1 opacity-70">saving…</div>}
      {error && <div data-testid="posture-error" className="px-2 py-1 text-red-500">{error}</div>}
    </div>
  );
};
